import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Avatar,
  Box,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import userIconYellow from "../../../assets/timetrails/user_icon_yellow.svg";
import "./TimeTrials.css";
import timeTrailsEmpty from "../../../assets/timetrails/time_trails_empty.svg";

export const TimeTrials = ({
  submissions,
  readOnly = false,
  hideTaskDescription = false,
}) => {
  const toBrowserUrl = (url = "") => {
    const trimmed = (url || "").trim();
    if (!trimmed) return "";
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };

  const [expanded, setExpanded] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [imageDisplaySize, setImageDisplaySize] = useState({
    width: null,
    height: null,
  });

  const handlePreviewImageLoad = (e) => {
    const img = e.target;
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    const maxW = window.innerWidth * 0.7;
    const maxH = window.innerHeight * 0.7; // leave room for title/close
    const scale = Math.min(maxW / naturalWidth, maxH / naturalHeight, 1);
    const width = Math.round(naturalWidth * scale);
    const height = Math.round(naturalHeight * scale);
    setImageDisplaySize({ width, height });
  };

  const handlePreviewClose = () => {
    setPreviewImage(null);
    setImageDisplaySize({ width: null, height: null });
  };

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "--";

    // Treat timezone-less ISO strings as UTC so display converts to local time.
    const normalized =
      typeof dateString === "string" &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateString) &&
      !/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(dateString)
        ? `${dateString}Z`
        : dateString;

    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return "--";

    return date.toLocaleString(undefined, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  // const getStatusColor = (status) => {
  //   switch (status) {
  //     case "PENDING":
  //       return "warning";
  //     case "APPROVED":
  //       return "success";
  //     case "REJECTED":
  //       return "error";
  //     default:
  //       return "default";
  //   }
  // };

  const handleApprove = async (submissionId) => {
    try {
      const submission = submissions.find((s) => s._id === submissionId);
      if (submission?.onApprove) {
        await submission.onApprove();
      }
    } catch (error) {
      console.error("Error approving submission:", error);
    }
  };

  const handleReject = async (submissionId) => {
    try {
      const submission = submissions.find((s) => s._id === submissionId);
      if (submission?.onReject) {
        await submission.onReject();
      }
    } catch (error) {
      console.error("Error rejecting submission:", error);
    }
  };

  return (
    <div className="timetrials-container">
      <Dialog
        open={Boolean(previewImage)}
        onClose={handlePreviewClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: imageDisplaySize.width ?? "auto",
            maxWidth: "70vw",
            height: imageDisplaySize.height
              ? imageDisplaySize.height + 52
              : "auto", // +52 for title bar
            maxHeight: "70vh",
            borderRadius: "12px",
            overflow: "hidden",
            "& .MuiDialogContent-root": { padding: 0 },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "4px 8px 4px 16px",
            flexShrink: 0,
            margin: 0,
          }}
        >
          <IconButton
            onClick={handlePreviewClose}
            size="small"
            sx={{ color: "#666" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: 0,
            margin: 0,
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            "&.MuiDialogContent-root": { padding: 0 },
            ...(imageDisplaySize.width && imageDisplaySize.height
              ? {
                  width: imageDisplaySize.width,
                  height: imageDisplaySize.height,
                  minWidth: imageDisplaySize.width,
                  minHeight: imageDisplaySize.height,
                }
              : { maxWidth: "70vw", maxHeight: "calc(70vh - 52px)" }),
          }}
        >
          {previewImage && (
            <img
              src={previewImage}
              alt="Attachment preview"
              onLoad={handlePreviewImageLoad}
              style={{
                width: imageDisplaySize.width ?? "auto",
                height: imageDisplaySize.height ?? "auto",
                maxWidth: "70vw",
                maxHeight: "calc(70vh - 52px)",
                objectFit: "cover",
                display: "block",
                margin: "0px 40px 0px 40px",
                marginBottom: "70px",
                backgroundColor: "red",
                overflow: "hidden",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      {submissions && submissions.length > 0 ? (
        submissions.map((submission, index) => (
          <Accordion
            key={submission._id}
            expanded={expanded === `panel${index}`}
            onChange={handleChange(`panel${index}`)}
            className="timetrial-accordion"
          >
            <AccordionSummary className="timetrial-summary">
              <div className="timetrial-header">
                <div className="timetrial-user-info">
                  <Avatar
                    alt={`${submission.user?.fname || "User"} ${
                      submission.user?.lname || ""
                    }`}
                    sx={{ width: 40, height: 40, marginRight: 2 }}
                    src={submission.user?.avatar || userIconYellow}
                    onError={(e) => {
                      e.target.src = userIconYellow;
                    }}
                  />
                  <div>
                    <Typography variant="h6" className="user-name">
                      {submission.user?.fname} {submission.user?.lname}
                    </Typography>
                    {hideTaskDescription ? (
                      <Typography
                        variant="body2"
                        className="submission-date submission-date--summary"
                      >
                        {formatDate(submission.createdAt)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" className="task-description">
                        {submission.timetrial?.task ||
                          "Complete the time trial task"}
                      </Typography>
                    )}
                  </div>
                </div>
                {expanded === `panel${index}` ? (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded(false);
                    }}
                    sx={{
                      color: "#666",
                      backgroundColor: "transparent",
                      width: "30px",
                      height: "30px",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <CloseIcon style={{ width: "18px", height: "18px" }} />
                  </IconButton>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      textTransform: "none",
                      color: "#3361CF",
                      backgroundColor: "rgba(51, 97, 207, 0.15)",
                      fontSize: "12px",
                      fontWeight: "bold",
                      width: "90px",
                      height: "30px",
                      border: "none",
                      "&:hover": {
                        backgroundColor: "#3361CF",
                        border: "none",
                        color: "white",
                      },
                    }}
                  >
                    View
                  </Button>
                )}
              </div>
            </AccordionSummary>
            <AccordionDetails className="timetrial-details">
              <div className="timetrial-content">
                <div className="timetrial-info">
                  {!hideTaskDescription ? (
                    <Typography variant="body2" className="submission-date">
                      <strong> Date - {formatDate(submission.createdAt)} </strong>
                    </Typography>
                  ) : null}

                  <div className="attachment-section">
                    <Typography variant="body2" className="attachment-label">
                      <strong>URL</strong>
                    </Typography>

                    {!submission.attachments?.url ? (
                      <p
                        style={{
                          color: "#666",
                          fontSize: "14px",
                          fontWeight: "800px",
                        }}
                      >
                        --
                      </p>
                    ) : (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          component="a"
                          href={toBrowserUrl(submission.attachments.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="attachment-link"
                        >
                          {submission.attachments.url}
                        </Typography>
                      </Box>
                    )}
                  </div>

                  <div className="attachment-section">
                    <Typography variant="body2" className="attachment-label">
                      <strong>Additional Note</strong>
                    </Typography>

                    {!submission.comments ? (
                      <p
                        style={{
                          color: "#666",
                          fontSize: "14px",
                          fontWeight: "800px",
                        }}
                      >
                        --
                      </p>
                    ) : (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          component="a"
                          rel="noopener noreferrer"
                          className="comments-text"
                        >
                          {submission?.comments}
                        </Typography>
                      </Box>
                    )}
                  </div>

                  {/* {submission.attachments?.filename && ( */}
                  <div className="attachment-section">
                    <Typography variant="body2" className="attachment-label">
                      <strong>Attachment</strong>
                    </Typography>

                    {!submission.attachments?.filename ? (
                      <p
                        style={{
                          color: "#666",
                          fontSize: "14px",
                          fontWeight: "800px",
                        }}
                      >
                        {" "}
                        --{" "}
                      </p>
                    ) : (
                      <Box
                        display="flex"
                        flexDirection="column"
                        maxWidth="fit-content"
                        gap={1}
                      >
                        <img
                          src={submission.attachments.filename}
                          alt="Submission attachment"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "200px",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                            objectFit: "contain",
                            marginTop: "4px",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            setPreviewImage(submission.attachments.filename)
                          }
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "block";
                          }}
                        />
                        <Typography
                          variant="body2"
                          component="a"
                          href={submission.attachments.filename}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="attachment-link"
                          style={{ display: "none" }}
                        >
                          {submission.attachments.filename}
                        </Typography>
                      </Box>
                    )}
                  </div>
                  {/* )} */}

                  {/* {submission.comments && (
                    <div className="comments-section">
                      <Typography variant="body2" className="comments-label">
                        <strong>Additional Note </strong>
                      </Typography>
                      <Typography variant="body2" className="comments-text">
                        {submission.comments}
                      </Typography>
                    </div>
                  )} */}
                </div>

                {submission.status === "PENDING" && !readOnly && (
                  <div className="timetrial-actions">
                    <Button
                      variant="outlined"
                      color="error"
                      className="reject-button"
                      onClick={() => handleReject(submission._id)}
                    >
                      Reject
                    </Button>
                    <Button
                      variant="outlined"
                      color="success"
                      className="approve-button"
                      onClick={() => handleApprove(submission._id)}
                    >
                      Approve
                    </Button>
                  </div>
                )}
              </div>
            </AccordionDetails>
          </Accordion>
        ))
      ) : (
        <div className="no-submissions">
          <div className="timetrails-task__main">
            <div className="timetrails-task__empty">
              <div className="empty-state-content">
                <div className="empty-state-image">
                  <img
                    src={timeTrailsEmpty}
                    alt="No Time Trials Found"
                    className="empty-illustration"
                  />
                </div>
                <div className="empty-state-text">
                  <h2 className="empty-title">No Submissions Found!</h2>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
